import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';

import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';

import {
	BUTTON_TYPES,
	EvCard,
	EvCSVExport,
	EvSection,
	EvSpinner,
	EvTitle,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues,
	useSkipLinks
} from '@evinced-private/ui-common';

import { useSetWebSdkBreadcrumbs } from 'src/apps/web-sdks/hooks/useSetWebSdkBreadcrumbs';
import { HorizontalDivider } from 'src/common/components/horizontal-divider/HorizontalDivider';
import { ProductFiltersPanel } from 'src/common/components/pinned-date-range-filters-panel';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { ReportInfo } from 'src/common/components/report-info/ReportInfo';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import {
	buildPanelInitialValues,
	mapPanelValuesToFilters,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { buildFullTableId } from 'src/common/helpers/TableStateHelper';
import { useEntityStatusQuery } from 'src/common/hooks/useEntityStatusQuery';
import {
	ENTITY_STATUS_DELETED,
	ENTITY_STATUS_LOADING,
	ENTITY_STATUS_SYNCING
} from 'src/common/hooks/useEntityStatusQuery.types';
import { useEnvironmentConfig } from 'src/common/hooks/useEnvironmentConfig';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import {
	WEB_SDK_TEST_ISSUES_TABLE_ID,
	WEB_SDK_TEST_TABLE_ID
} from 'src/common/pages/dashboards-container/dashboard_consts';
import {
	DASHBOARD_ERROR_MSG,
	WEB_SDK_TEST_INFO_ERROR_MSG
} from 'src/common/pages/dashboards-container/errorMessages';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import {
	WEB_SDK_ISSUES,
	WEB_SDK_TEST_DETAILS
} from 'src/common/providers/reactQueryProvider/QueryKeys';
import { usePropertiesMapQuery } from 'src/common/services/properties/hooks/usePropertiesMapQuery';
import { DevelopmentToggles, getSingleToggle } from 'src/common/services/TogglesService';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { WebSdkTestIssuesSkeleton } from '../../components/skeletons';
import {
	DELETED_TEST_STATE_KEY,
	NAME_NOT_PROVIDED,
	WEB_SDK_TEST_ISSUES_CSV_FILE_NAME_PREFIX,
	webSdkIssuesFilterDefinitions
} from '../../helpers/WebSdkConsts';
import { getWebSdkInfoItems } from '../../helpers/WebSdkHelper';
import { getWebSdkTestIssuesColumns } from '../../helpers/WebSdkTableHelper';
import { useWebSdkIssuesFilterOptionsQuery } from '../../hooks/useWebSdkIssuesFilterOptionsQuery';
import { useWebSdkIssuesQuery } from '../../hooks/useWebSdkIssuesQuery';
import { getWebSdkTestDetails, getWebSdkTestIssues } from '../../services/WebSdkDashboardService';
import { WebSdkIssuesPageError } from '../error-pages/WebSdkIssuesPageError';

import { mapWebSdkIssuesToCsvRows } from './WebSdkTestIssuesCsvHelper';
import { LABELS, WEB_SDK_TEST_ISSUES_CSV_HEADERS } from './WebSdkTestIssuesPage.labels';

import './WebSdkTestIssuesPage.scss';

const enableWebSdkProperties = getSingleToggle(DevelopmentToggles.ENABLE_WEB_SDK_PROPERTIES);

export const WebSdkTestIssuesPage: FC = () => {
	const history = useHistory();
	const queryClient = useQueryClient();
	const { getTableStateById, onFilterChange } = useTableState();
	const { runId } = useParams<{ [key: string]: string }>();

	usePlatformConfigInitializer();

	const { unitIdDisplayName, unitIdLabelConfig, isNewPlatformEnabled } = usePlatformConfigStore();
	const { environmentDisplayName, environmentLabelConfig } = useEnvironmentConfig();
	const tableId = useMemo(() => buildFullTableId(WEB_SDK_TEST_ISSUES_TABLE_ID, runId), [runId]);
	const { filters: testsTableFilters } = getTableStateById(WEB_SDK_TEST_TABLE_ID);
	const tableState = getTableStateById(tableId);
	const { filters, tableSort, paginationInfo } = tableState;

	const filtersWithPropertiesFromTestsTable = useMemo(
		() => ({
			...filters,
			// if properties are enabled and there's a property filter applied
			// in the tests page, it should be applied here too
			...(enableWebSdkProperties
				? {
						properties:
							(filters.properties?.length ? filters.properties : testsTableFilters?.properties) ??
							[]
					}
				: {})
		}),
		[filters, testsTableFilters?.properties]
	);

	const { query: testDetailsQuery, status: testStatus } = useEntityStatusQuery({
		queryKey: [WEB_SDK_TEST_DETAILS, runId],
		queryFn: () => getWebSdkTestDetails(runId)
	});

	useEffect(() => {
		if (testStatus === ENTITY_STATUS_DELETED) {
			history.push(RoutesHelper.getWebSdkDashboardPath(), { [DELETED_TEST_STATE_KEY]: true });
		}
	}, [testStatus, history]);

	useSetWebSdkBreadcrumbs(runId, testDetailsQuery.data?.name, testDetailsQuery?.data?.runTime);
	useSkipLinks(!testDetailsQuery.isLoading);

	const issuesFilterOptionsQuery = useWebSdkIssuesFilterOptionsQuery(runId);

	const propertiesQuery = usePropertiesMapQuery(enableWebSdkProperties);

	const isPropertiesLoading = enableWebSdkProperties && propertiesQuery.isLoading;

	const propertiesOptions = useMemo((): OptionType[] => {
		if (!enableWebSdkProperties || isPropertiesLoading || testDetailsQuery.isLoading) {
			return [];
		}

		const options: OptionType[] = [];
		testDetailsQuery.data?.propertyIds.forEach((propertyId) => {
			if (propertiesQuery.data?.has(propertyId)) {
				options.push({
					value: propertyId,
					label: propertiesQuery.data.get(propertyId) as string
				});
			}
		});

		return options;
	}, [
		isPropertiesLoading,
		propertiesQuery.data,
		testDetailsQuery.data?.propertyIds,
		testDetailsQuery.isLoading
	]);

	const issuesQuery = useWebSdkIssuesQuery({
		runId,
		filters: filtersWithPropertiesFromTestsTable,
		sort: tableSort?.[0],
		page: paginationInfo.page,
		pageSize: paginationInfo.sizePerPage
	});

	const [showInitialSkeleton, setShowInitialSkeleton] = useState(true);
	const isFilterOptionsLoading = isPropertiesLoading || issuesFilterOptionsQuery.isLoading;

	const isPrimaryLoading =
		testStatus === ENTITY_STATUS_LOADING ||
		testStatus === ENTITY_STATUS_SYNCING ||
		isFilterOptionsLoading ||
		issuesQuery.isLoading;

	useEffect(() => {
		if (!isPrimaryLoading && showInitialSkeleton) {
			setShowInitialSkeleton(false);
		}
	}, [isPrimaryLoading, showInitialSkeleton]);

	const handleApply = (appliedFilters: TEvFiltersPanelValues): void => {
		onFilterChange(tableId, mapPanelValuesToFilters(appliedFilters));
	};

	const total = issuesQuery.data?.total ?? 0;
	const unfilteredTotal = issuesQuery.data?.unfilteredTotal ?? 0;

	const testName = testDetailsQuery.data?.name || NAME_NOT_PROVIDED;

	const fetchCSVData = useCallback(async (): Promise<string[][]> => {
		const result = await queryClient.fetchQuery({
			queryKey: [
				WEB_SDK_ISSUES,
				runId,
				filtersWithPropertiesFromTestsTable,
				tableSort?.[0],
				0,
				total
			],
			queryFn: () =>
				getWebSdkTestIssues({
					runId,
					filterOptions: filtersWithPropertiesFromTestsTable,
					sort: tableSort?.[0],
					page: 0,
					pageSize: total
				}),
			staleTime: Infinity
		});
		return mapWebSdkIssuesToCsvRows(result?.rows ?? []);
	}, [queryClient, runId, filtersWithPropertiesFromTestsTable, tableSort, total]);

	const csvExportProps = useMemo(
		() => ({
			triggerButtonProps: {
				type: BUTTON_TYPES.SECONDARY,
				children: LABELS.DOWNLOAD_CSV,
				title: LABELS.DOWNLOAD_CSV
			},
			fileName: `${WEB_SDK_TEST_ISSUES_CSV_FILE_NAME_PREFIX}-${runId}`,
			headers: Object.values(WEB_SDK_TEST_ISSUES_CSV_HEADERS),
			fetchCSVData
		}),
		[runId, fetchCSVData]
	);

	const testInfoSection = useMemo(() => {
		if (testDetailsQuery.isError || (enableWebSdkProperties && propertiesQuery.isError)) {
			return <EvCard title={DASHBOARD_ERROR_MSG}>{WEB_SDK_TEST_INFO_ERROR_MSG}</EvCard>;
		}

		return (
			<>
				<EvTitle titleText={testName} headingLevel={1} className="main-title page-heading" />
				<EvSection
					ariaLabel={`Test ${testName} info`}
					skipLinkId="metadata-section"
					ariaLive="polite"
					className={classNames('dashboard-section', 'web-sdk-test-info-section')}
				>
					<ReportInfo
						titleText="Test Info"
						reportInfo={getWebSdkInfoItems(
							propertiesQuery.data,
							isNewPlatformEnabled,
							unitIdDisplayName,
							unitIdLabelConfig,
							environmentDisplayName,
							environmentLabelConfig
						)}
						reportDetails={testDetailsQuery.data ?? undefined}
					/>
				</EvSection>
			</>
		);
	}, [
		testDetailsQuery.isError,
		testDetailsQuery.data,
		propertiesQuery.isError,
		propertiesQuery.data,
		testName,
		isNewPlatformEnabled,
		unitIdDisplayName,
		unitIdLabelConfig,
		environmentDisplayName,
		environmentLabelConfig
	]);

	const panelDefinitions = useMemo(() => mapToPanelDefinitions(webSdkIssuesFilterDefinitions), []);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filtersWithPropertiesFromTestsTable),
		[panelDefinitions, filtersWithPropertiesFromTestsTable]
	);

	if (showInitialSkeleton) {
		return <WebSdkTestIssuesSkeleton />;
	}

	const remoteOptions = {
		...(issuesFilterOptionsQuery.data ?? {}),
		properties: propertiesOptions ?? []
	};

	const isTableLoading =
		isPropertiesLoading ||
		testDetailsQuery.isLoading ||
		issuesQuery.isLoading ||
		issuesQuery.isPreviousData;

	const renderTableAndFilters = (): React.ReactNode => {
		if (issuesQuery.isError || issuesFilterOptionsQuery.isError) {
			return <WebSdkIssuesPageError />;
		}

		if (!issuesQuery.isLoading && !issuesQuery.isPreviousData && unfilteredTotal === 0) {
			return <WebSdkIssuesPageError isNoData />;
		}

		return (
			<>
				<EvSection
					ariaLabel="WEB SDK Test's issues filters"
					ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
					skipLinkId="filters-section"
					ariaLive="polite"
					className={classNames('dashboard-section', 'web-sdk-test-issues-filters-section')}
				>
					{isFilterOptionsLoading ? (
						<EvSpinner />
					) : (
						<div className="filters-area">
							<div className="filter-line">
								<ProductFiltersPanel
									appElement={PLATFORM_APP_ID}
									filterDefinitions={panelDefinitions}
									initialValues={initialValues}
									filterOptions={remoteOptions}
									onApply={handleApply}
									onReset={handleApply}
								/>
							</div>
						</div>
					)}
				</EvSection>
				<EvSection
					ariaLabel="WEB SDK Test's issues Table"
					skipLinkId="main-section"
					className={classNames('dashboard-section', 'issues-table-section')}
				>
					<div className="page-heading-container">
						<EvTitle titleText="Detected issues" headingLevel={2} className="page-heading" />

						{total > 0 && <EvCSVExport {...csvExportProps} />}
					</div>
					{isTableLoading ? (
						<TableSkeleton columns={6} rowCount={10} />
					) : (
						<PlatformTable
							isTableLoading={issuesQuery.isLoading || issuesQuery.isPreviousData}
							tableId={tableId}
							tableData={issuesQuery.data?.rows ?? []}
							totalCount={unfilteredTotal}
							totalPaginationableResults={total}
							className="test-issues-table"
							columns={getWebSdkTestIssuesColumns(runId, testDetailsQuery.data?.runTime, testName)}
							options={{
								keyField: 'id',
								title: 'Web SDK Test Issues',
								caption: 'Web SDK Test Issues table',
								pagination: true,
								dataType: 'issues',
								remote: true
							}}
							withUrlSwitcher={true}
						/>
					)}
				</EvSection>
			</>
		);
	};

	return (
		<div className="web-sdk-test-issues-page">
			{testInfoSection}
			<HorizontalDivider />
			{renderTableAndFilters()}
		</div>
	);
};
